import { Component, ViewChild, AfterViewInit, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { HttpClient, HttpHeaders, HttpClientModule } from "@angular/common/http";
import {
  DayPilot,
  DayPilotModule,
  DayPilotCalendarComponent,
  DayPilotMonthComponent,
  DayPilotNavigatorComponent
} from "@daypilot/daypilot-lite-angular";
import { DataService } from "./data.service";

// PrimeNG imports
import { DialogModule } from "primeng/dialog";
import { AutoCompleteModule } from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { DropdownModule } from 'primeng/dropdown';

import { ConfirmationService } from 'primeng/api';
import { AppointmentService } from '../../../service/admin/appointment.service';
import { ClinicService } from '../../../service/admin/clinic.service';
import { UserService } from '../../../service/admin/user.service';
import { PatientRegistrationService } from '../../../service/admin/patient-registration.service';
import { ApiService } from '../../../service/admin/api.service';
import { ToasterService } from '../../../service/common/toaster.service';


@Component({
  selector: "app-calendar",
  standalone: true,
  imports: [
    DayPilotModule, CommonModule, FormsModule, HttpClientModule,
    DialogModule, AutoCompleteModule, ButtonModule, DropdownModule
  ],
  providers: [DataService, AppointmentService, ConfirmationService, ClinicService, UserService, PatientRegistrationService, ApiService],
  template: `
    <div class="calendar-shell">
<div class="calendar-topbar">
        <div class="calendar-title-wrap">
          <h2 class="calendar-title">Appointment Calendar</h2>
          <p class="calendar-subtitle">Manage slots and bookings with a clearer day, week, and month view.</p>
        </div>
        <div class="calendar-meta">
          <span class="meta-chip">
            <i class="pi pi-calendar"></i>
            {{ selectedDate | date:'dd MMM yyyy' }}
          </span>
          <span class="meta-chip">
            <i class="pi pi-users"></i>
            {{ events.length }} appointments
          </span>
        </div>
      </div>

      <div class="calendar-toolbar">
        <div class="view-switch" role="tablist" aria-label="Calendar view mode">
          <button type="button" class="view-btn" (click)="viewDay()" [class.active]="viewMode === 'Day'">Day</button>
          <button type="button" class="view-btn" (click)="viewWeek()" [class.active]="viewMode === 'Week'">Week</button>
          <button type="button" class="view-btn" (click)="viewMonth()" [class.active]="viewMode === 'Month'">Month</button>
        </div>

        <div class="calendar-legend">
          <span class="legend-item"><span class="legend-dot st-scheduled"></span>Scheduled</span>
          <span class="legend-item"><span class="legend-dot st-assistant"></span>Assistant Doctor</span>
          <span class="legend-item"><span class="legend-dot st-doctor"></span>Doctor</span>
          <span class="legend-item"><span class="legend-dot st-medicine"></span>Medicine</span>
          <span class="legend-item"><span class="legend-dot st-completed"></span>Completed</span>
          <span class="legend-item"><span class="legend-dot st-cancelled"></span>Cancelled</span>
        </div>
      </div>

      <div class="calendar-board">
        <div class="calendar-canvas">
          <daypilot-calendar
            *ngIf="viewMode === 'Day'"
            [config]="configDay"
            [events]="events"
            #day>
          </daypilot-calendar>

          <daypilot-calendar
            *ngIf="viewMode === 'Week'"
            [config]="configWeek"
            [events]="events"
            #week>
          </daypilot-calendar>

          <daypilot-month
            *ngIf="viewMode === 'Month'"
            [config]="configMonth"
            [events]="events"
            #month>
          </daypilot-month>
        </div>
      </div>

    </div>

    <!-- Patient Selection Dialog -->
    <p-dialog 
      header="Select Patient" 
      [(visible)]="patientDialogVisible" 
      [modal]="true" 
      [style]="{width: '60vw'}"
      styleClass="calendar-dialog">

      <div class="schedule-dialog-shell">
        <div class="schedule-dialog-head">
          <div>
            <!-- <h4>Schedule Appointment</h4> -->
            <p>Select patient, appointment type, date, and an available slot.</p>
          </div>
          <div class="schedule-head-chips">
            <span class="head-chip available">
              <i class="pi pi-check-circle"></i>
              {{ availableSlotsCount }} available
            </span>
            <span class="head-chip blocked">
              <i class="pi pi-lock"></i>
              {{ blockedSlotsCount }} blocked
            </span>
          </div>
        </div>

        <div class="schedule-card">
          <div class="row">
            <div class="col-md-5 mb-3">
              <label class="form-label d-block">Patient</label>
              <p-autoComplete 
                [(ngModel)]="selectedPatient" 
                [suggestions]="patients"
                (completeMethod)="searchPatients($event)" 
                (onSelect)="onPatientSelect($event)"
                field="full_name"
                placeholder="Search by name, number, or contact"
                [forceSelection]="true"
                emptyMessage="No patients found"
                class="w-100">
              </p-autoComplete>
            </div>
            
            <div class="col-md-4 mb-3">
              <label for="doctor" class="form-label d-block">Appointment Type</label>
              <p-dropdown 
                id="appointment_type"
                name="appointment_type"
                [options]="appointmentType" 
                [(ngModel)]="appointment.appointment_type" 
                optionLabel="label"
                optionValue="value"
                placeholder="Appointment Type" 
                class="w-100"/>
            </div>

            <div class="col-md-3 mb-3">
              <label for="appointment_date" class="form-label d-block">Appointment Date</label>
              <input type="date" class="form-control schedule-date-input" id="appointment_date" name="appointment_date" (change)="generateTimeSlots()" [(ngModel)]="appointment.appointment_date">
            </div>
          </div>
          <div class="row">
            <div class="col-md-12 mb-1">
              <div class="collect-later-wrap mt-1">
                <label class="collect-later-switch" for="collect_later">
                  <input
                    class="collect-later-input"
                    type="checkbox"
                    id="collect_later"
                    name="collect_later"
                    [(ngModel)]="appointment.collect_later">
                  <span class="collect-later-track" aria-hidden="true"></span>
                  <span class="collect-later-copy">
                    <span class="collect-later-title">Collect medicine later</span>
                    <span class="collect-later-subtitle">Keep appointment flow unchanged, mark pickup for later.</span>
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div class="schedule-card">
          <label class="form-label d-block">Select Time Slot</label>
          <div class="row slot-grid">
            <div class="col-3" *ngFor="let slot of timeSlots">
              <button 
                type="button"
                class="slot-btn" 
                [class.selected]="selectedSlot === slot"
                [disabled]="slot.disabled"
                (click)="selectSlot(slot)">
                {{ slot.label }}
              </button>
            </div>
          </div>
          <div class="slot-selection-preview" *ngIf="selectedSlot">
            <i class="pi pi-clock"></i>
            Selected: <strong>{{ selectedSlot?.label }}</strong>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <button pButton label="Cancel" class="p-button-text calendar-dialog-cancel" (click)="patientDialogVisible=false"></button>
        <button pButton label="Confirm" class="calendar-dialog-confirm"
          [disabled]="!selectedPatient || !appointment?.appointment_type || !appointment?.appointment_date || !selectedSlot"
          (click)="confirmPatientSelection()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    :host {
      display: block;
    }

    .calendar-shell {
      min-height: 100%;
      padding: 14px;
      background:
        radial-gradient(1000px 260px at 8% -12%, rgba(14, 165, 233, 0.14), transparent 55%),
        radial-gradient(1000px 260px at 95% -15%, rgba(16, 185, 129, 0.12), transparent 60%),
        #f6f9ff;
    }

    .calendar-topbar {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 12px;
    }

    .calendar-title {
      margin: 0;
      font-size: 24px;
      font-weight: 800;
      color: #0f172a;
      letter-spacing: 0.2px;
    }

    .calendar-subtitle {
      margin: 4px 0 0;
      color: #475569;
      font-size: 13px;
    }

    .calendar-meta {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .meta-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border-radius: 999px;
      border: 1px solid #cfe0f7;
      background: #ffffff;
      color: #1e3a8a;
      padding: 6px 11px;
      font-size: 12px;
      font-weight: 700;
      box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
    }

    .calendar-toolbar {
      position: sticky;
      top: 0;
      z-index: 9;
      padding: 8px 0 10px;
      background: linear-gradient(180deg, rgba(246, 249, 255, 0.95), rgba(246, 249, 255, 0.74));
      backdrop-filter: blur(5px);
    }

    .view-switch {
      display: inline-flex;
      border: 1px solid #c8d9ef;
      border-radius: 14px;
      overflow: hidden;
      box-shadow: 0 8px 16px rgba(15, 23, 42, 0.06);
      background: #ffffff;
    }

    .view-btn {
      border: 0;
      border-right: 1px solid #dbe7f5;
      background: #ffffff;
      color: #334155;
      height: 38px;
      min-width: 92px;
      padding: 0 14px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.18s ease;
    }

    .view-btn:last-child {
      border-right: 0;
    }

    .view-btn:hover {
      background: #f1f7ff;
      color: #1e3a8a;
    }

    .view-btn.active {
      background: linear-gradient(135deg, #0284c7, #0ea5e9);
      color: #ffffff;
      box-shadow: inset 0 -2px 0 rgba(255, 255, 255, 0.2);
    }

    .calendar-board {
      border: 1px solid #dbe7f5;
      border-radius: 14px;
      background: #ffffff;
      padding: 10px;
      box-shadow: 0 14px 24px rgba(15, 23, 42, 0.08);
    }

    .calendar-canvas {
      min-height: 600px;
      border-radius: 10px;
      overflow: hidden;
    }

    .calendar-shell ::ng-deep .calendar_default_event,
    .calendar-shell ::ng-deep .month_default_event {
      border-radius: 10px !important;
      border: 1px solid rgba(15, 23, 42, 0.14) !important;
      box-shadow: 0 8px 16px rgba(15, 23, 42, 0.14) !important;
    }

    .calendar-shell ::ng-deep .calendar_default_event_inner,
    .calendar-shell ::ng-deep .month_default_event_inner {
      padding: 4px 6px !important;
      font-size: 12px !important;
      font-weight: 700 !important;
      letter-spacing: 0.1px;
    }

    .calendar-shell ::ng-deep .calendar_default_colheader,
    .calendar-shell ::ng-deep .month_default_colheader {
      background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%) !important;
      color: #1e3a8a !important;
      font-weight: 800 !important;
      font-size: 12px !important;
    }

    .calendar-shell ::ng-deep .calendar_default_corner,
    .calendar-shell ::ng-deep .calendar_default_rowheader {
      background: #f8fbff !important;
      color: #475569 !important;
      font-weight: 700 !important;
      font-size: 12px !important;
    }

    .calendar-shell ::ng-deep .calendar_default_cell,
    .calendar-shell ::ng-deep .month_default_cell {
      border-color: #e2e8f0 !important;
    }

    .calendar-shell ::ng-deep .calendar_default_now {
      background: rgba(14, 165, 233, 0.08) !important;
    }

    .calendar-legend {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      margin-top: 10px;
    }

    .legend-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      border-radius: 999px;
      border: 1px solid #dbe5f0;
      background: #ffffff;
      color: #334155;
      padding: 3px 10px;
      font-size: 11px;
      font-weight: 700;
    }

    .legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 999px;
      border: 1px solid rgba(15, 23, 42, 0.2);
    }

    .legend-dot.st-scheduled { background: #e4f3ff; }
    .legend-dot.st-assistant { background: #e7d8ff; }
    .legend-dot.st-doctor { background: #ffd9cc; }
    .legend-dot.st-medicine { background: #e6f7f7; }
    .legend-dot.st-completed { background: #d9f7be; }
    .legend-dot.st-cancelled { background: #ffb3a8; }

    .slot-btn {
      width: 100%;
      border: 1px solid #bfdbfe;
      background: #eff6ff;
      color: #1e3a8a;
      border-radius: 10px;
      padding: .55rem .7rem;
      margin-bottom: .7rem;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      transition: all 0.18s ease;
    }

    .slot-btn:hover:not(:disabled) {
      background: #dbeafe;
      transform: translateY(-1px);
    }

    .slot-btn.selected {
      background: linear-gradient(135deg, #0284c7, #0ea5e9);
      border-color: #0284c7;
      color: #ffffff;
      box-shadow: 0 8px 16px rgba(14, 165, 233, 0.34);
    }

    .slot-btn:disabled {
      background: #e5e7eb;
      border-color: #d1d5db;
      cursor: not-allowed;
      color: #6b7280;
    }

    :host ::ng-deep .calendar-dialog .p-dialog-content {
      background: #f8fbff;
    }

    :host ::ng-deep .calendar-dialog .p-dialog-footer {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 10px;
    }

    .schedule-dialog-shell {
      display: grid;
      gap: 10px;
    }

    .schedule-dialog-head h4 {
      margin: 0;
      font-size: 20px;
      font-weight: 800;
      color: #0f172a;
    }

    .schedule-dialog-head {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 8px;
    }

    .schedule-dialog-head p {
      margin: 4px 0 0;
      color: #475569;
      font-size: 13px;
    }

    .schedule-head-chips {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      flex-wrap: wrap;
    }

    .head-chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      border-radius: 999px;
      border: 1px solid transparent;
      padding: 4px 10px;
      font-size: 11px;
      font-weight: 700;
    }

    .head-chip.available {
      color: #166534;
      background: #dcfce7;
      border-color: #bbf7d0;
    }

    .head-chip.blocked {
      color: #7c2d12;
      background: #ffedd5;
      border-color: #fdba74;
    }

    .schedule-card {
      border: 1px solid #dbe7f5;
      border-radius: 12px;
      background: #ffffff;
      padding: 12px;
      box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
    }

    .schedule-card .form-label {
      font-size: 12px;
      font-weight: 700;
      color: #334155;
    }

    .schedule-date-input {
      border: 1px solid #cbd5e1;
      border-radius: 10px;
      height: 38px;
      font-size: 13px;
    }

    .calendar-shell ::ng-deep .schedule-card .p-autocomplete,
    .calendar-shell ::ng-deep .schedule-card .p-dropdown {
      width: 100%;
    }

    .calendar-shell ::ng-deep .schedule-card .p-autocomplete .p-inputtext,
    .calendar-shell ::ng-deep .schedule-card .p-dropdown .p-dropdown-label {
      height: 38px;
      display: flex;
      align-items: center;
      border-radius: 10px;
      font-size: 13px;
    }

    .collect-later-wrap {
      border: 1px solid #dbe7f5;
      background: linear-gradient(180deg, #f8fbff 0%, #f1f7ff 100%);
      border-radius: 12px;
      padding: 10px 12px;
    }

    .collect-later-switch {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0;
      cursor: pointer;
      user-select: none;
    }

    .collect-later-input {
      position: absolute;
      opacity: 0;
      width: 1px;
      height: 1px;
      pointer-events: none;
    }

    .collect-later-track {
      position: relative;
      width: 46px;
      height: 26px;
      border-radius: 999px;
      border: 1px solid #b6c9e6;
      background: #dbe7f5;
      transition: background-color 0.2s ease, border-color 0.2s ease;
      flex-shrink: 0;
    }

    .collect-later-track::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      border-radius: 999px;
      background: #ffffff;
      box-shadow: 0 2px 6px rgba(15, 23, 42, 0.2);
      transition: transform 0.2s ease;
    }

    .collect-later-input:checked + .collect-later-track {
      background: linear-gradient(135deg, #0f766e, #14b8a6);
      border-color: #0f766e;
    }

    .collect-later-input:checked + .collect-later-track::after {
      transform: translateX(20px);
    }

    .collect-later-copy {
      display: flex;
      flex-direction: column;
      gap: 1px;
      line-height: 1.2;
    }

    .collect-later-title {
      color: #0f172a;
      font-size: 13px;
      font-weight: 700;
    }

    .collect-later-subtitle {
      color: #64748b;
      font-size: 11px;
      font-weight: 500;
    }

    .collect-later-input:focus-visible + .collect-later-track {
      outline: 2px solid #0ea5e9;
      outline-offset: 2px;
    }

    .slot-selection-preview {
      margin-top: 6px;
      border: 1px solid #bfdbfe;
      background: #eff6ff;
      color: #1e3a8a;
      border-radius: 10px;
      padding: 8px 10px;
      font-size: 12px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    :host ::ng-deep .calendar-dialog-confirm {
      background: linear-gradient(135deg, #0f766e, #14b8a6);
      border-color: #0f766e;
      color: #ffffff;
      border-radius: 12px;
      min-width: 112px;
      height: 40px;
      font-size: 13px;
      font-weight: 700;
      box-shadow: 0 8px 16px rgba(15, 118, 110, 0.28);
      transition: transform 0.16s ease, box-shadow 0.2s ease, filter 0.2s ease;
    }

    :host ::ng-deep .calendar-dialog-confirm:hover:not(:disabled) {
      transform: translateY(-1px);
      filter: brightness(1.03);
      box-shadow: 0 10px 20px rgba(15, 118, 110, 0.34);
    }

    :host ::ng-deep .calendar-dialog-confirm:disabled {
      opacity: 0.55;
      box-shadow: none;
      cursor: not-allowed;
    }

    :host ::ng-deep .calendar-dialog-cancel {
      color: #334155;
      background: #ffffff;
      border: 1px solid #cbd5e1;
      border-radius: 12px;
      min-width: 104px;
      height: 40px;
      font-size: 13px;
      font-weight: 700;
      transition: transform 0.16s ease, border-color 0.2s ease, background-color 0.2s ease;
    }

    :host ::ng-deep .calendar-dialog-cancel:hover {
      transform: translateY(-1px);
      border-color: #94a3b8;
      background: #f8fafc;
    }

    @media (max-width: 992px) {
      .calendar-topbar {
        flex-direction: column;
      }

      .calendar-canvas {
        min-height: 500px;
      }

      .schedule-dialog-head {
        flex-direction: column;
      }
    }
  `]
})
export class CalendarComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("day") day!: DayPilotCalendarComponent;
  @ViewChild("week") week!: DayPilotCalendarComponent;
  @ViewChild("month") month!: DayPilotMonthComponent;
  @ViewChild("navigator") nav!: DayPilotNavigatorComponent;

  public appointment: any = {
    id: 0,
    clinic_id: 0,
    patient_id: 0,
    doctor_id: 0,
    appointment_type: '',
    appointment_date: new Date(Date.now() + 19800000).toISOString().slice(0, 10),
    start_time: '',
    end_time: '',
    collect_later: false,
    // status: '',
    notes: ''
  }

  events: DayPilot.EventData[] = [];
  date = DayPilot.Date.today();
  viewMode: "Day" | "Week" | "Month"  = "Week";

  // Patient selection state
  patientDialogVisible = false;
  selectedPatient: any;
  patients: any[] = [];
  pendingEventArgs: any;
  public clinicList: any[] = [];
  public doctorsList: any[] = [];

  timeSlots: any[] = [];
  selectedSlot: any;
  selectedDate= new Date(Date.now() + 19800000).toISOString().slice(0, 10);

  public queue: any[] = [];
  public medicineQueue: any[] = [];

  public appointmentType: any = [
    { label: 'New', value: 'new' },
    { label: 'Follow Up', value: 'follow up' },
  ]

  private apiUrl: string | undefined;

  get availableSlotsCount(): number {
    return (this.timeSlots || []).filter((slot: any) => !slot?.disabled).length;
  }

  get blockedSlotsCount(): number {
    return (this.timeSlots || []).filter((slot: any) => !!slot?.disabled).length;
  }

  // contextMenu = new DayPilot.Menu({
  //   items: [
  //     { text: "Delete", onClick: args => args.source.calendar.events.remove(args.source) },
  //     { text: "Edit...", onClick: async args => {
  //         const modal = await DayPilot.Modal.prompt("Edit event text:", args.source.data.text);
  //         if (modal.result) {
  //           args.source.data.text = modal.result;
  //           args.source.calendar.events.update(args.source);
  //         }
  //       }
  //     },
  //     { text: "-" },
  //     { text: "Red", onClick: args => this.updateColor(args, DataService.colors.red) },
  //     { text: "Green", onClick: args => this.updateColor(args, DataService.colors.green) },
  //     { text: "Blue", onClick: args => this.updateColor(args, DataService.colors.blue) },
  //     { text: "Yellow", onClick: args => this.updateColor(args, DataService.colors.yellow) },
  //     { text: "Gray", onClick: args => this.updateColor(args, DataService.colors.gray) }
  //   ]
  // });

  constructor(
    private ds: DataService, 
    private appointmentService: AppointmentService, 
    private http: HttpClient, 
    private toast: ToasterService,
    private cdr: ChangeDetectorRef,
    private clinicService: ClinicService,
    private userService: UserService,
    private patientRegistrationService: PatientRegistrationService, 
    private apiService: ApiService
  ) {
    this.apiUrl = this.apiService.getBaseUrl();
  }

  private intervalId: any;

  ngOnInit() {
    this.loadEvents();
    this.getClinic();
    this.getDoctors();
    this.generateTimeSlots();   // ✅ load slots on startup

    // ✅ Auto-refresh slots every minute
    this.intervalId = setInterval(() => {
      this.generateTimeSlots();
      this.loadEvents();
    }, 30 * 1000);
  }

  ngAfterViewInit() {
    setTimeout(() => this.loadEvents(), 0);
  }

  // --- View switching ---
  viewDay(): void { this.viewMode = "Day"; this.loadEvents(); }
  viewWeek(): void { this.viewMode = "Week"; this.loadEvents(); }
  viewMonth(): void { this.viewMode = "Month"; this.loadEvents(); }

  // --- Event loading ---
  loadEvents(): void {
    let dp: any;
    if (this.viewMode === "Day") dp = this.day?.control;
    else if (this.viewMode === "Week") dp = this.week?.control;
    else if (this.viewMode === "Month") dp = this.month?.control;
    if (!dp) return;

    const from = dp.visibleStart().toString("yyyy-MM-dd");
    const to = dp.visibleEnd().toString("yyyy-MM-dd");

    
    this.appointmentService.getAppointment( from, to ).subscribe({
      next: (result: any) => {
        // 🔹 Transform API response into DayPilot events
        this.events = result.data.map((appt: any) => ({
          id: appt.id,
          text: `${appt.patient.first_name} ${appt.patient.last_name} (${appt.patient.patient_number})`,
          start: `${appt.appointment_date}T${appt.start_time}`,
          end: `${appt.appointment_date}T${appt.end_time}`,
          backColor: this.getStatusColor(appt.status),
          tags: {
            status: appt.status,
            notes: appt.notes,
            doctor: `${appt.doctor.first_name} ${appt.doctor.last_name}`,
            clinic: appt.clinic.name,
            patientId: appt.patient_id,
            doctorId: appt.doctor_id,
            clinicId: appt.clinic_id
          }
        }));

        // console.log("Loaded appointments:", this.events);
        dp.update({ events: this.events });
      },
      error: (err) => {
        console.error("Error loading appointments:", err);
        dp.update({ events: [] });
      }
    });
  }

  // --- Calendar Configs --- 
  configDay: DayPilot.CalendarConfig = {
    viewType: "Day",
    durationBarVisible: false,
    // contextMenu: this.contextMenu,
    onTimeRangeSelected: this.onTimeRangeSelected.bind(this),
    onBeforeEventRender: this.onBeforeEventRender.bind(this),
    // onEventClick: this.onEventClick.bind(this)
  };

  configWeek: DayPilot.CalendarConfig = {
    viewType: "Week",
    durationBarVisible: true,
    // contextMenu: this.contextMenu,
    onTimeRangeSelected: this.onTimeRangeSelected.bind(this),
    onBeforeEventRender: this.onBeforeEventRender.bind(this),
    // onEventClick: this.onEventClick.bind(this)
  };

  configMonth: DayPilot.MonthConfig = {
    // contextMenu: this.contextMenu,
    eventBarVisible: false,
    onTimeRangeSelected: this.onTimeRangeSelected.bind(this),
    // onEventClick: this.onEventClick.bind(this)
  };

  // --- Appointment creation ---
  async onTimeRangeSelected(args: any) {
    this.pendingEventArgs = args;
    this.patientDialogVisible = true; // show dialog instead of DayPilot.Modal
  }

  private getAuthHeaders() {
        const token = localStorage.getItem('jwtToken');
        return {
          headers: new HttpHeaders({
            'Authorization' : `Bearer ${token}`
          })
        }
      }

  getClinic() {
    this.clinicService.getClinic().subscribe({
      next: (res: any) => {
        this.clinicList = Array.isArray(res.data)
          ? res.data.map((clinic:any) => ({
              label: clinic.name,
              value: clinic.id
            }))
          : [];
      },
      error: (err) => {
        console.error('Clinic API error:', err);
        this.clinicList = [];
      }
    });
  }

  getDoctors() {
    this.userService.getDoctors().subscribe({
      next: (res: any) => {
        this.doctorsList = Array.isArray(res.data)
          ? res.data.map((doctor:any) => ({
              label: doctor.name,
              value: doctor.id
            }))
          : [];
      },
      error: (err) => {
        console.error('Doctor API error:', err);
        this.doctorsList = [];
      }
    });
  }

  // searchPatients(event: any) {
  //   const query = event.query;
  //   this.http.get<any>('https://clinic.mdssuicideprevention.in/api/patient/search', { params: { q: query }, ...this.getAuthHeaders()})
  //     .subscribe(res => {
  //       if (!res.data || res.data.length === 0) {
  //       this.patients = [];
  //       this.toast.add({
  //         severity: 'info',
  //         summary: 'No patients found',
  //         detail: `No patients found for "${query}".`
  //       });
  //     } else {
  //       this.patients = res.data.map((p: any) => ({
  //         ...p,
  //         full_name: `${p.full_name} (${p.patient_number})`
  //       }));
  //     }
  //   });
  // }

  searchPatients(event: any) {
  const query = event.query;
  this.http.get<any>(this.apiUrl + '/patient/search', { params: { q: query }, ...this.getAuthHeaders()})
    .subscribe(res => {
      if (!res.data || res.data.length === 0) {
        this.patients = [];
        this.toast.add({
          severity: 'info',
          summary: 'No patients found',
          detail: `No patients found for "${query}".`
        });
      } else {
        this.patients = res.data.map((p: any) => ({
          ...p,
          full_name: `${p.first_name} ${p.middle_name} ${p.last_name} (${p.patient_number})`
        }));
      }
      // Stop spinner and update view
      this.cdr.detectChanges();
    }, error => {
      this.patients = [];
      this.toast.add({
        severity: 'info',
        summary: 'Info',
        detail: 'No patients found.'
      });
      this.cdr.detectChanges();
    });
}

  // confirmPatientSelection() {
  //   if (!this.pendingEventArgs || !this.selectedPatient) return;

  //   const args = this.pendingEventArgs;
  //   args.control.clearSelection();

  //   args.control.events.add(new DayPilot.Event({
  //     start: args.start,
  //     end: args.end,
  //     id: DayPilot.guid(),
  //     text: `${this.selectedPatient.full_name}`
  //   }));

  //   this.patientDialogVisible = false;
  //   this.selectedPatient = null;
  //   this.pendingEventArgs = null;
  // }

  confirmPatientSelection() {
    if (!this.pendingEventArgs || !this.selectedPatient) return;

    const args = this.pendingEventArgs;
    args.control.clearSelection();

    // Prepare appointment data
    const appointmentData = {
      patient_id: this.selectedPatient.id || this.selectedPatient.patient_id,
      // clinic_id: this.appointment.clinic_id,
      // doctor_id: this.appointment.doctor_id,
      clinic_id: 1,
      doctor_id: 1,
      appointment_type: this.appointment.appointment_type,
      appointment_date: this.appointment.appointment_date,
      start_time: this.appointment.start_time,
      end_time: this.appointment.end_time,
      collect_later: Boolean(this.appointment.collect_later),
      status: this.appointment.status,
      notes: this.appointment.notes
    };

    this.appointmentService.addAppointment(appointmentData).subscribe({
      next: (created) => {
        // Add the event to the calendar after successful creation
        args.control.events.add(new DayPilot.Event({
          start: args.start,
          end: args.end,
          // @ts-ignore
          id: created.id || DayPilot.guid(),
          text: `${this.selectedPatient.first_name} ${this.selectedPatient.last_name} (${this.selectedPatient.patient_number})`
        }));
        this.patientDialogVisible = false;
        this.selectedPatient = null;
        this.pendingEventArgs = null;
        this.loadEvents();
        this.toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Appointment created successfully.'
        });
      },
      error: (err) => {
        this.toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to create appointment.'
        });
      }
    });
  }

  onBeforeEventRender(args: any) {
    args.data.areas = [
      {
        top: 3, right: 3, width: 20, height: 20,
        symbol: "/icons/daypilot.svg#minichevron-down-2",
        fontColor: "#fff", toolTip: "Show context menu", action: "ContextMenu"
      },
      {
        top: 3, right: 25, width: 20, height: 20,
        symbol: "/icons/daypilot.svg#x-circle",
        fontColor: "#fff", action: "None", toolTip: "Delete event",
        onClick: (args2: any) => args2.control.events.remove(args2.source)
      }
    ];
  }

  onPatientSelect(event: any) {
  // event.value is the selected patient object
    this.appointment.patient_id = event.value.id || event.value.patient_id;
    // Optionally, store the whole patient object if needed
    this.selectedPatient = event.value;
    // console.log('Selected patient_id:', this.appointment.patient_id);
  }

  updateColor(args: any, color: string) {
    const event = args.source;
    event.data.backColor = color;
    event.calendar.events.update(event);
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'scheduled': 
        return '#e4f3ff';
      case 'assistant_doctor': 
        return '#e7d8ff';
      case 'doctor': 
        return '#ffd9cc';
      case 'medicine': 
        return '#e6f7f7';
      case 'completed': 
        return '#d9f7be';
      case 'cancelled': 
        return '#ffb3a8';
      default: 
        return '#d1dce5ff'; // fallback for unknown statuses
    }
  }

  // async onEventClick(args: any) {
  //   const form = [
  //     { name: "Text", id: "text" },
  //     { name: "Start", id: "start", dateFormat: "MM/dd/yyyy", type: "datetime" },
  //     { name: "End", id: "end", dateFormat: "MM/dd/yyyy", type: "datetime" },
  //     { name: "Color", id: "backColor", type: "select", options: this.ds.getColors() }
  //   ];
  //   const modal = await DayPilot.Modal.form(form, args.e.data);
  //   if (!modal.canceled) args.control.events.update(modal.result);
  // }

  //! ============== Time slots code ===========================

  ngOnDestroy(): void {
    // clear interval when component is destroyed
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  // called whenever user changes the date
  onDateChange(date: string) {
    this.appointment.appointment_date = date;
    this.generateTimeSlots();
  }

  generateTimeSlots() {
    const slots = [];
    const now = new Date();
    const startHour = 9;
    const endHour = 23; // 9 PM
    

    for (let hour = startHour; hour < endHour; hour++) {
      const start = this.formatTime(hour);
      const end = this.formatTime(hour + 1);

      const slotEnd = new Date();
      slotEnd.setHours(hour + 1, 0, 0, 0);

      slots.push({
        label: `${this.formatAmPm(hour)} - ${this.formatAmPm(hour + 1)}`,
        start,
        end,
        disabled: slotEnd <= now   // ✅ disable if the slot has already ended
      });
    }

    if (this.appointment?.appointment_date) {
      const [year, month, day] = this.appointment.appointment_date.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day);

      const today = new Date();

      // If selected date is before today → disable all slots
      if (selectedDate < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
        slots.forEach(slot => slot.disabled = true);
      }
      // If selected date is after today → enable all slots
      else if (selectedDate > new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
        slots.forEach(slot => slot.disabled = false);
      }
      // If selected date is today → keep your default slotEnd <= now logic
    }

    this.timeSlots = slots;
  }

  formatTime(hour: number): string {
    return `${hour.toString().padStart(2, '0')}:00`;
  }

  formatAmPm(hour: number): string {
    const suffix = hour >= 12 ? "PM" : "AM";
    const displayHour = ((hour + 11) % 12 + 1);
    return `${displayHour}:00 ${suffix}`;
  }

  selectSlot(slot: any) {
    if (slot.disabled) return; // prevent picking disabled slots

    this.selectedSlot = slot;
    this.appointment.start_time = slot.start;
    this.appointment.end_time = slot.end;
  }

  //! ================== Load queues =============================

  loadQueue() {
    // const date = this.selectedDate || new Date().toISOString().slice(0, 10);
    const date = this.selectedDate || new Date(Date.now() + 19800000).toISOString().slice(0, 10);

    this.appointmentService.getQueue({
      clinic_id: 1,
      doctor_id: 1,
      date: date,
      // date: "2025-09-12",
      slot: 60
    }).subscribe((res: any) => {
      if (res.success) {
        const appointments = res.data.slots.flatMap((slot: any) =>
          slot.appointments.map((appt: any) => ({
            appointment_id: appt.id,
            patient_id: appt.patient_id,
            clinic_id: appt.clinic_id,
            doctor_id: appt.doctor_id,
            time: appt.start_time,
            status: appt.status
          }))
        );

        // Collect all patient IDs
        const patientIds = appointments.map((a: any) => a.patient_id);

        // Fetch all patient data in one request
        this.patientRegistrationService.getPatientsBatch(patientIds)
          .subscribe((patientRes: any) => {
            if (patientRes.success) {
              const patientsMap = new Map();
              patientRes.data.forEach((p: any) => patientsMap.set(p.id, p));

              // Map appointments to patients
              this.queue = appointments.map((appt: any) => {
                const patient = patientsMap.get(appt.patient_id);
                return {
                  name: `${patient.first_name} ${patient.last_name}`,
                  full_name: patient.full_name,
                  time: appt.time,
                  status: appt.status,
                  primary_contact: patient.primary_contact,
                  secondary_contact: patient.secondary_contact,
                  is_free: patient.is_free,
                  age: patient.age,
                  appointment_id: appt.appointment_id,
                  clinic_id: appt.clinic_id,
                  doctor_id: appt.doctor_id,
                };
              });

              // Sort by time once
              this.queue.sort((a, b) => a.time.localeCompare(b.time));
              // console.log('Loaded queue:', this.queue);
            }
          });
      }
    });
  }

  loadMedicineQueue() {
    // const date = this.selectedDate || new Date().toISOString().slice(0, 10);
    const date = this.selectedDate || new Date(Date.now() + 19800000).toISOString().slice(0, 10);

    this.appointmentService.getMedicineQueue({
      clinic_id: 1,
      doctor_id: 1,
      date: date,
      // date: "2025-09-12",
      slot: 60
    }).subscribe((res: any) => {
      if (res.success) {
        const appointments = res.data.slots.flatMap((slot: any) =>
          slot.appointments.map((appt: any) => ({
            appointment_id: appt.id,
            patient_id: appt.patient_id,
            clinic_id: appt.clinic_id,
            doctor_id: appt.doctor_id,
            time: appt.start_time,
            status: appt.status
          }))
        );

        // Collect all patient IDs
        const patientIds = appointments.map((a: any) => a.patient_id);

        // Fetch all patient data in one request
        this.patientRegistrationService.getPatientsBatch(patientIds)
          .subscribe((patientRes: any) => {
            if (patientRes.success) {
              const patientsMap = new Map();
              patientRes.data.forEach((p: any) => patientsMap.set(p.id, p));

              // Map appointments to patients
              this.medicineQueue = appointments.map((appt: any) => {
                const patient = patientsMap.get(appt.patient_id);
                return {
                  name: `${patient.salutation} ${patient.full_name}`,
                  full_name: patient.full_name,
                  time: appt.time,
                  status: appt.status,
                  contact_number: patient.contact_number,
                  salutation: patient.salutation,
                  is_free: patient.is_free,
                  card_id: patient.card_id,
                  age: patient.age,
                  appointment_id: appt.appointment_id,
                  clinic_id: appt.clinic_id,
                  doctor_id: appt.doctor_id,
                  referred_by: patient.referred_by,
                  referred_by_dr: patient.referred_by_dr,
                  occupation: patient.occupation
                };
              });

              // Sort by time once
              this.medicineQueue.sort((a, b) => a.time.localeCompare(b.time));
              // console.log('Loaded queue:', this.medicineQueue);
            }
          });
      }
    });
  }


}
